import type { ContactPoint, Location, HealthcareService } from "fhir/r3"
import { getRescheduleInstructions } from "../../fhirUtils/healthcareServiceUtils"
import "./ContactDetails.scss"
import { useMemo } from "react"

const ContactLink = ({ contactPoint }: { contactPoint: ContactPoint }) => {
  const linkProps = useMemo(() => {
    if (!contactPoint || !contactPoint.system || !contactPoint.value) {
      return {}
    }

    const formattedValue = contactPoint.value.replace(/\s+/g, "")

    switch (contactPoint.system) {
      case "phone":
        return {
          href: `tel:+44${formattedValue}`,
          ariaDescription: `Phone this organisation on ${contactPoint.value}`,
        }
      case "fax":
        return {
          href: `fax:+44${formattedValue}`,
          ariaDescription: `Fax this organisation on ${contactPoint.value}`,
        }
      case "pager":
        return {
          href: `pager:+44${formattedValue}`,
          ariaDescription: `Page this organisation on ${contactPoint.value}`,
        }
      case "email":
        return {
          href: `mailto:${contactPoint.value}`,
          ariaDescription: `Email this organisation via ${contactPoint.value}`,
        }
      case "sms":
        return {
          href: `sms:+44${formattedValue}`,
          ariaDescription: `Text this organisation on ${contactPoint.value}`,
        }
      case "url":
        return {
          href: contactPoint.value,
          ariaDescription: `Visit this organisation online at ${contactPoint.value}`,
        }
      default:
        return {
          ariaDescription: `Contact this organisation via ${contactPoint.value}`,
        }
    }
  }, [contactPoint])

  const { href, ariaDescription } = linkProps

  if (href) {
    return (
      <a aria-label={ariaDescription} href={href}>
        {contactPoint.value}
      </a>
    )
  }

  return <p aria-label={ariaDescription}>{contactPoint.value}</p>
}

interface Props {
  healthcareService: HealthcareService
  header: string
  location?: Location
}

export function ContactDetails({ header, healthcareService, location }: Props): JSX.Element | null {
  const changeInstructions = getRescheduleInstructions(healthcareService)

  const getContactPoints = (): ContactPoint[] | undefined => {
    const points = []
    const contactResource = location?.telecom ? location : healthcareService

    const phone = contactResource.telecom?.find((it) => it.system === "phone")
    if (phone) {
      points.push(phone)
    }

    const email = contactResource.telecom?.find((it) => it.system === "email")
    if (email) {
      points.push(email)
    }
    return points
  }

  const contactPoints = getContactPoints()

  if (!contactPoints) return null

  return (
    <>
      <h2 className="nhsuk-heading-l">{header}</h2>
      {changeInstructions.map((instruction) => (
        <p key={instruction}>{instruction}</p>
      ))}
      <ul>
        {contactPoints?.map((contactType) => {
          return (
            <li key={contactType.system}>
              {contactType.system === "phone" ? "call" : contactType.system} <ContactLink contactPoint={contactType} />
            </li>
          )
        })}
      </ul>
    </>
  )
}
